<template>
  <div class="dom-panel" v-loading="loading">
    <el-alert class="dom-note" type="info" :closable="false" show-icon :title="$t(T.OVERVIEW)" />
    <el-alert
      v-if="feedback.message"
      class="dom-note"
      :type="feedback.type"
      :closable="false"
      show-icon
      :title="feedback.message" />
    <div class="dom-toolbar">
      <el-select
        v-model="platform"
        :disabled="saving"
        :placeholder="$t('전체 플랫폼')"
        clearable
        @change="chooseFirstLink">
        <el-option v-for="p in platforms" :key="p" :value="p" :label="platformName(p)" />
      </el-select>
      <el-input
        v-model="query"
        :disabled="saving"
        clearable
        :placeholder="$t('메뉴·옵션그룹·배달앱 옵션 검색')"
        @input="chooseFirstLink" />
      <el-button :disabled="saving" icon="el-icon-refresh" @click="reload()">{{ $t('다시 조회') }}</el-button>
    </div>
    <el-select
      v-model="selectedLinkId"
      class="dom-menu"
      filterable
      :disabled="saving"
      :placeholder="$t('비교할 메뉴를 선택하세요')"
      @change="chooseFirstOption">
      <el-option v-for="link in filteredLinks" :key="link.linkId" :value="link.linkId" :label="linkLabel(link)" />
    </el-select>
    <p v-if="!selectedLink" class="dom-empty">{{ $t(links.length ? '검색 결과가 없습니다.' : T.EMPTY) }}</p>
    <template v-else>
      <div class="dom-context">
        <span>{{ platformName(selectedLink.platform) }} · {{ selectedLink.shopName || selectedLink.shopNo }}</span>
        <span>{{ $t('오더엔 메뉴') }}: {{ selectedLink.menuName || $t('메뉴 연결 필요') }}</span>
        <span
          >{{ $t('배달앱 메뉴') }}: {{ selectedLink.platformMenuName || '-' }} (#{{
            selectedLink.catalogMenuRef
          }})</span
        >
        <el-button type="text" :disabled="saving" @click="showHistory">{{ $t('변경 이력') }}</el-button>
      </div>
      <el-alert
        v-if="!selectedLink.observationAvailable"
        class="dom-note"
        type="warning"
        :closable="false"
        :title="$t(T.UNREAD)" />
      <div class="dom-columns">
        <section>
          <h3>{{ $t('오더엔 옵션 · 현재 연결') }}</h3>
          <el-input v-model="sourceQuery" :disabled="saving" clearable :placeholder="$t('오더엔 옵션·그룹 검색')" />
          <el-table
            ref="sourceTable"
            :data="sourceOptions"
            border
            highlight-current-row
            height="360"
            @current-change="selectOption"
            :row-key="(row) => row.smenuOptCd"
            :empty-text="$t('이 메뉴에 적용된 오더엔 옵션이 없습니다.')">
            <el-table-column :label="$t('옵션 / 그룹')" min-width="140">
              <template slot-scope="s">
                <strong>{{ s.row.name }}</strong>
                <div class="dom-muted">{{ s.row.groupName }} · #{{ s.row.smenuOptCd }}</div>
                <div>{{ money(s.row.price) }}</div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('현재 연결')" min-width="150">
              <template slot-scope="s">
                <el-tag size="mini" :type="statusOf(s.row.status).type">{{ $t(statusOf(s.row.status).text) }}</el-tag>
                <div v-if="s.row.connected">
                  {{ s.row.connected.groupName }} / {{ s.row.connected.target.itemName }}
                </div>
                <div v-else-if="s.row.target" class="dom-muted">{{ $t('이전 대상') }}: {{ s.row.target.itemName }}</div>
                <small>{{ $t(s.row.source === 'MANUAL' ? '수동 지정' : '자동 대응') }}</small>
              </template>
            </el-table-column>
          </el-table>
        </section>
        <section>
          <h3>{{ $t('배달앱 옵션 · 연결 대상 선택') }}</h3>
          <el-input
            v-model="candidateQuery"
            :disabled="saving"
            clearable
            :placeholder="$t('배달앱 옵션·그룹·추가금액 검색')" />
          <el-table :data="candidates" border height="360" :empty-text="$t(T.NO_CANDIDATE)">
            <el-table-column width="50" align="center">
              <template slot-scope="s">
                <el-radio
                  v-model="selectedTargetKey"
                  :label="targetKey(s.row.target)"
                  :disabled="!canChoose(s.row)"
                  @change="review = null"
                  ><span class="dom-sr">{{ s.row.target.itemName }}</span></el-radio
                >
              </template>
            </el-table-column>
            <el-table-column :label="$t('옵션 / 그룹')" min-width="170">
              <template slot-scope="s">
                <strong>{{ s.row.target.itemName }}</strong>
                <div class="dom-muted">{{ s.row.groupName || '-' }} · {{ s.row.target.groupRef }}</div>
                <small>{{ s.row.target.itemRef || $t('그룹 안의 유일한 이름으로 구분') }}</small>
                <div v-if="!s.row.selectable" class="dom-warning">{{ $t('대상 식별 정보 확인 필요') }}</div>
                <div v-else-if="!canSelect(s.row, selectedOptionCd)" class="dom-warning">
                  {{ $t('다른 오더엔 옵션에 연결됨') }}
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('추가금액')" width="100" align="right"
              ><template slot-scope="s">{{ money(s.row.price) }}</template></el-table-column
            >
          </el-table>
        </section>
      </div>
      <div v-if="selectedOption" class="dom-selection">
        <p>
          {{ selectedOption.groupName }} / <strong>{{ selectedOption.name }}</strong> →
          {{ chosenTarget ? `${chosenTarget.groupName || '-'} / ${chosenTarget.target.itemName}` : $t('대상 미선택') }}
        </p>
        <p v-if="selectedOption.reason" class="dom-muted">{{ $t(reasonOf(selectedOption.reason)) }}</p>
        <p class="dom-muted">{{ $t('이 옵션을 사용하는 메뉴') }}: {{ menuNames(selectedOption.menus) }}</p>
        <el-button
          type="primary"
          :disabled="
            !canEdit || !chosenTarget || saving || selectedLink.linkState !== 'LINKED' || selectedOption.useYn === 'N'
          "
          @click="prepareSave(false)"
          >{{ $t(selectedOption.connected ? '연결 변경' : '선택한 옵션 연결') }}</el-button
        >
        <el-button
          :disabled="
            !canEdit || saving || selectedLink.linkState !== 'LINKED' || selectedOption.reason === 'MANUALLY_UNLINKED'
          "
          @click="prepareSave(true)"
          >{{ $t('연결 해제') }}</el-button
        >
        <p v-if="!canEdit" class="dom-muted">{{ $t(T.READ_ONLY) }}</p>
      </div>
    </template>

    <el-dialog
      :visible="!!review"
      :title="$t('옵션 연결 확인')"
      width="680px"
      append-to-body
      :close-on-click-modal="false"
      :close-on-press-escape="!saving"
      :show-close="!saving"
      @close="cancelReview">
      <template v-if="review">
        <p>{{ linkLabel(review.link) }}</p>
        <p class="dom-muted">
          {{ $t('오더엔 메뉴') }} #{{ review.link.smenuCd }} · {{ $t('배달앱 메뉴') }} #{{ review.link.catalogMenuRef }}
        </p>
        <el-descriptions :column="1" border>
          <el-descriptions-item :label="$t('오더엔 옵션')"
            >{{ review.option.groupName }} / {{ review.option.name }} ·
            {{ money(review.option.price) }}</el-descriptions-item
          >
          <el-descriptions-item :label="$t('변경 전')">{{
            review.option.connected
              ? targetLabel(review.option.connected.target, review.option.connected.groupName)
              : $t('연결 없음')
          }}</el-descriptions-item>
          <el-descriptions-item :label="$t('변경 후')">{{
            review.target
              ? `${targetLabel(review.target.target, review.target.groupName)} · ${money(review.target.price)}`
              : $t('연결 해제')
          }}</el-descriptions-item>
          <el-descriptions-item :label="$t('변경 범위')">{{ $t(T.SCOPE) }}</el-descriptions-item>
          <el-descriptions-item v-if="review.option.connected" :label="$t('기존 대상 그룹을 쓰는 메뉴')">{{
            menuNames(review.option.connected.sharedMenus)
          }}</el-descriptions-item>
          <el-descriptions-item v-if="review.target" :label="$t('새 대상 그룹을 쓰는 메뉴')">{{
            menuNames(review.target.sharedMenus)
          }}</el-descriptions-item>
        </el-descriptions>
        <el-alert class="dom-note" type="warning" :closable="false" show-icon :title="$t(T.OUTBOUND)" />
        <p>{{ $t(T.SAVE_EFFECT) }}</p>
      </template>
      <div slot="footer">
        <el-button :disabled="saving" @click="cancelReview">{{ $t('취소') }}</el-button>
        <el-button type="primary" :loading="saving" :disabled="!canEdit" @click="save">{{
          $t('확인하고 저장')
        }}</el-button>
      </div>
    </el-dialog>
    <el-dialog
      :visible.sync="historyVisible"
      :title="$t('옵션 연결 변경 이력 · 최근 50건')"
      width="850px"
      append-to-body>
      <el-alert v-if="historyError" type="error" :closable="false" :title="historyError" />
      <el-table :data="history" border v-loading="historyLoading">
        <el-table-column prop="actor" :label="$t('변경 담당자')" width="125" />
        <el-table-column prop="changedAt" :label="$t('변경 시각')" min-width="155" />
        <el-table-column :label="$t('오더엔 옵션')" min-width="150">
          <template slot-scope="s">{{ historySource(s.row.before, s.row.smenuOptCd) }}</template>
        </el-table-column>
        <el-table-column :label="$t('변경 전')" min-width="170"
          ><template slot-scope="s">{{ historyTarget(s.row.before) }}</template></el-table-column
        >
        <el-table-column :label="$t('변경 후')" min-width="170"
          ><template slot-scope="s">{{ historyTarget(s.row.after) }}</template></el-table-column
        >
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import {
  DELIVERY_MENU_API,
  deliveryMenuPlatformName,
  deliveryMenuErrorMessage,
  optionMappingStatus,
  optionMappingReason,
} from '@/utils/deliveryMenuSync'
import { OPTION_MAPPING_TEXT } from '@/utils/deliveryMenuNotes'
import {
  optionTargetKey,
  matchesOptionSearch,
  filterMappingLinks,
  canSelectOptionTarget,
  mappingHistoryTarget,
  mappingHistorySource,
  mappingTargetLabel,
  selectMappingLink,
  mappingSaveFeedback,
} from '@/utils/deliveryOptionMapping'

export default {
  name: 'DeliveryOptionMappingPanel',
  mixins: [mainVueMixin],
  props: {
    storeCd: { type: Number, required: true },
    initialSmenuCd: { type: Number, default: null },
    initialOptionCd: { type: Number, default: null },
  },
  data() {
    return {
      T: OPTION_MAPPING_TEXT,
      links: [],
      loading: false,
      saving: false,
      sequence: 0,
      platform: '',
      query: '',
      sourceQuery: '',
      candidateQuery: '',
      selectedLinkId: null,
      selectedOptionCd: null,
      selectedTargetKey: '',
      review: null,
      saveReceipt: null,
      refreshError: null,
      history: [],
      historyVisible: false,
      historyLoading: false,
      historyError: '',
    }
  },
  computed: {
    feedback() {
      return mappingSaveFeedback(this.saveReceipt, this.refreshError, (text) => this.$t(text))
    },
    canEdit() {
      return this.checkAuthActionAccess('DeliveryOptionMappingUp')
    },
    platforms() {
      return [...new Set(this.links.map((l) => l.platform))]
    },
    filteredLinks() {
      return filterMappingLinks(this.links, this.platform, this.query)
    },
    selectedLink() {
      return this.filteredLinks.find((l) => l.linkId === this.selectedLinkId) || null
    },
    sourceOptions() {
      return ((this.selectedLink && this.selectedLink.options) || []).filter((o) =>
        matchesOptionSearch([o.name, o.groupName, o.smenuOptCd], this.sourceQuery)
      )
    },
    selectedOption() {
      return this.sourceOptions.find((o) => o.smenuOptCd === this.selectedOptionCd) || null
    },
    candidates() {
      return ((this.selectedLink && this.selectedLink.candidates) || []).filter((c) =>
        matchesOptionSearch([c.groupName, c.target.itemName, c.price], this.candidateQuery)
      )
    },
    chosenTarget() {
      return (
        ((this.selectedLink && this.selectedLink.candidates) || []).find(
          (c) => optionTargetKey(c.target) === this.selectedTargetKey && canSelectOptionTarget(c, this.selectedOptionCd)
        ) || null
      )
    },
  },
  watch: {
    storeCd: {
      immediate: true,
      handler() {
        this.links = []
        this.review = null
        this.saveReceipt = null
        this.refreshError = null
        this.historyVisible = false
        this.reload()
      },
    },
  },
  beforeDestroy() {
    this.sequence += 1
  },
  methods: {
    platformName: deliveryMenuPlatformName,
    targetKey: optionTargetKey,
    canSelect: canSelectOptionTarget,
    statusOf: optionMappingStatus,
    reasonOf: optionMappingReason,
    historyTarget: mappingHistoryTarget,
    historySource: mappingHistorySource,
    targetLabel: mappingTargetLabel,
    money(value) {
      return value == null ? this.$t('읽지 못함') : `${this.util.formatMoney(value)}${this.$t('원')}`
    },
    menuNames(refs) {
      return (refs || []).map((m) => m.smenuNm || `#${m.smenuCd}`).join(', ') || '-'
    },
    linkLabel(link) {
      return `${deliveryMenuPlatformName(link.platform)} · ${link.menuName || link.platformMenuName || '-'} ↔ ${
        link.platformMenuName || '-'
      } · ${link.shopName || link.shopNo} · ${link.menuBoardRef || link.catalogMenuRef || link.linkId}`
    },
    canChoose(candidate) {
      return (
        this.canEdit &&
        !this.saving &&
        !!this.selectedOption &&
        this.selectedOption.useYn !== 'N' &&
        this.selectedLink.linkState === 'LINKED' &&
        canSelectOptionTarget(candidate, this.selectedOptionCd)
      )
    },
    chooseFirstLink() {
      this.review = null
      this.selectedLinkId = (this.filteredLinks[0] || {}).linkId || null
      this.chooseFirstOption()
    },
    chooseFirstOption(preserveSelection = false) {
      this.sourceQuery = ''
      this.candidateQuery = ''
      this.review = null
      const options = (this.selectedLink && this.selectedLink.options) || []
      const option =
        (preserveSelection && options.find((o) => o.smenuOptCd === this.selectedOptionCd)) ||
        options.find((o) => o.smenuOptCd === this.initialOptionCd) ||
        options[0]
      this.selectOption(option, true)
      this.$nextTick(() => {
        if (this.$refs.sourceTable) this.$refs.sourceTable.setCurrentRow(option)
      })
    },
    selectOption(option, refresh = false) {
      if (this.saving && refresh !== true) return
      this.selectedOptionCd = option ? option.smenuOptCd : null
      this.selectedTargetKey = option && option.connected ? optionTargetKey(option.connected.target) : ''
      this.review = null
    },
    async reload() {
      const sequence = ++this.sequence
      this.loading = true
      this.review = null
      this.refreshError = null
      try {
        const res = await this.$axios.get(`${DELIVERY_MENU_API}/option-mappings`, {
          params: { storeCd: this.storeCd },
          logoutOn403: false,
          showErrorMessage: false,
        })
        if (sequence !== this.sequence) return false
        this.links = (res.data && res.data.links) || []
        const selected = selectMappingLink(this.filteredLinks, {
          selectedLinkId: this.selectedLinkId,
          initialSmenuCd: this.initialSmenuCd,
          initialOptionCd: this.initialOptionCd,
        })
        this.selectedLinkId = selected ? selected.linkId : null
        this.chooseFirstOption(true)
        return true
      } catch (error) {
        if (sequence !== this.sequence) return false
        this.links = []
        this.selectedLinkId = null
        this.selectedOptionCd = null
        this.selectedTargetKey = ''
        this.refreshError = error
        return false
      } finally {
        if (sequence === this.sequence) this.loading = false
      }
    },
    prepareSave(unlink) {
      if (!this.canEdit || !this.selectedOption || this.saving) return
      this.review = { link: this.selectedLink, option: this.selectedOption, target: unlink ? null : this.chosenTarget }
    },
    cancelReview() {
      if (!this.saving) this.review = null
    },
    async save() {
      if (!this.review || this.saving || !this.canEdit) return
      const { link, option, target } = this.review
      const storeCd = this.storeCd
      this.saving = true
      this.saveReceipt = null
      this.refreshError = null
      this.$emit('busy', true)
      try {
        const receipt = await this.$axios
          .put(
            '/api/admin/delivery-menu/option-mappings',
            {
              linkId: link.linkId,
              smenuCd: link.smenuCd,
              smenuOptTpCd: option.smenuOptTpCd,
              smenuOptCd: option.smenuOptCd,
              revision: link.revision,
              reviewToken: link.reviewToken,
              target: target ? target.target : null,
            },
            { logoutOn403: false, showErrorMessage: false }
          )
          .then(
            () => ({ ok: true }),
            (error) => ({ ok: false, error })
          )
        if (storeCd !== this.storeCd) return
        this.review = null
        this.saveReceipt = receipt
        // 응답이 유실됐을 수도 있으므로 실제 상태를 읽되 자동으로 저장을 반복하지 않는다.
        await this.reload()
        if (storeCd === this.storeCd && receipt.ok) this.$emit('changed')
      } finally {
        this.saving = false
        this.$emit('busy', false)
      }
    },
    async showHistory() {
      if (!this.selectedLink) return
      const linkId = this.selectedLink.linkId
      const storeCd = this.storeCd
      const sequence = this.sequence
      const current = () => storeCd === this.storeCd && sequence === this.sequence && this.selectedLinkId === linkId
      this.history = []
      this.historyError = ''
      this.historyVisible = true
      this.historyLoading = true
      try {
        const res = await this.$axios.get(`${DELIVERY_MENU_API}/option-mappings/${linkId}/history`, {
          logoutOn403: false,
          showErrorMessage: false,
        })
        if (current()) this.history = res.data || []
      } catch (error) {
        if (current()) this.historyError = deliveryMenuErrorMessage(error)
      } finally {
        if (current()) this.historyLoading = false
      }
    },
  },
}
</script>

<style scoped>
.dom-toolbar,
.dom-context {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 12px 0;
}
.dom-toolbar > .el-input {
  width: 340px;
}
.dom-menu {
  width: 100%;
}
.dom-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.dom-columns section {
  min-width: 0;
}
.dom-columns h3 {
  font-size: 15px;
  margin: 14px 0 8px;
}
.dom-columns .el-table {
  margin-top: 8px;
}
.dom-note {
  margin: 12px 0;
}
.dom-muted {
  color: #707780;
  font-size: 12px;
}
.dom-warning {
  color: #a05b00;
  font-size: 12px;
}
.dom-context {
  font-size: 13px;
}
.dom-selection {
  padding: 10px 0;
}
.dom-empty {
  padding: 32px;
  text-align: center;
  color: #707780;
}
.dom-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
@media (max-width: 900px) {
  .dom-columns {
    grid-template-columns: 1fr;
  }
}
</style>
